import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaBuilding, FaProjectDiagram, FaChartLine, FaUsers, FaFileContract, FaCheck, FaArrowRight, FaHeadset, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import BrandText from '../styles/brandStyles';

const Inforte = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isSticky, setIsSticky] = useState(false);

  // Función para manejar el scroll y actualizar la sección activa
  useEffect(() => {
    const handleScroll = () => {
      // Detectar si el submenú debe ser sticky
      const heroSection = document.getElementById('overview');
      const headerHeight = 64; // Altura del header principal
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      } else {
        // Si no se encuentra la sección, mantener el menú sticky para evitar que desaparezca
        setIsSticky(true);
      }

      // Detectar qué sección está visible
      const sections = ['overview', 'features', 'services', 'benefits', 'cta'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140; // Altura del header + submenu
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      // Si no se encuentra ninguna sección activa, mantener la última activa
      if (!activeFound && window.scrollY > 0) {
        // Mantener la sección activa anterior o establecer una por defecto
        setIsSticky(true);
      }
    };

    // Ejecutar handleScroll inmediatamente para establecer el estado inicial
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Función para hacer scroll a una sección
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140; // Altura del header + submenu
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="INFORTE - Información de Proyectos y Obras | ECOSOFT"
        description="El servicio de información de proyectos y obras más completo del mercado. Localiza oportunidades de negocio, contacta clientes potenciales y haz crecer tu empresa."
        keywords="INFORTE, proyectos, obras, construcción, licitaciones, clientes potenciales, información de mercado"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-2xl text-white">
            <div className="flex items-center mb-6">
              <div className="relative w-auto h-auto mr-6 flex-shrink-0">
                <div className="bg-white/90 rounded-lg px-8 py-3 pt-4 shadow-lg">
                  <img 
                    src="/images/inforte-logo.svg" 
                    alt="INFORTE" 
                    className="h-12 w-auto object-contain" 
                    onError={(e) => {
                      e.target.src = '/images/logos/LOGO_Inforte.png';
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
              
                <h2 className="text-2xl font-medium mt-2">El servicio de información más completo del mercado</h2>
              </div>
            </div>
            <p className="text-xl mb-8">
              Contrata obras o proyectos y contacta a tus próximos clientes. La diferencia entre buscar y encontrar.
            </p>
            <div className="flex gap-4">
              <a href="https://www.inforte.com.mx" target="_blank" rel="noopener noreferrer" className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors">
                Visitar sitio oficial
              </a>
              <button onClick={() => scrollToSection('cta')} className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors">
                Solicitar demostración
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Submenu Navigation */}
      <div 
        className={`bg-white shadow-md transition-all duration-300 sticky-nav ${
          isSticky 
            ? 'fixed top-[64px] left-0 right-0 z-30 border-t border-gray-200' 
            : 'relative'
        }`}
        style={{ minHeight: '56px' }} // Altura mínima para evitar saltos
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex overflow-x-auto py-4 items-center relative">
            <div className="flex-grow flex">
              <button 
                onClick={() => scrollToSection('overview')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'overview' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Resumen
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'features' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Características
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'services' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Servicios
              </button>
              <button 
                onClick={() => scrollToSection('benefits')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'benefits' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Beneficios
              </button>
              <button 
                onClick={() => scrollToSection('cta')}
                className={`px-6 py-2 whitespace-nowrap font-medium ${
                  activeSection === 'cta' 
                    ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                    : 'text-gray-600 hover:text-[#0066CC]'
                }`}
              >
                Comenzar
              </button>
            </div>
            <div className="flex items-center ml-4">
              <div className="bg-white/90 rounded-lg px-4 py-2 shadow-sm">
                <img 
                  src="/images/inforte-logo.svg" 
                  alt="INFORTE" 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.target.src = '/images/logos/LOGO_Inforte.png';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <section id="features" className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">La diferencia entre buscar y encontrar</h2>
          
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-xl text-gray-700 mb-6">
              Tú tienes el mejor producto/servicio para la Industria de la Construcción en México y nosotros contamos con la mejor base de datos a nivel nacional.
            </p>
            <p className="text-xl text-gray-700">
              Conoce qué, a quién, dónde y cuándo ofrecer tus servicios. Y si no sabes cómo hacerlo, ¡nosotros te auxiliamos!
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaBuilding className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Información de primera mano</h3>
              <p className="text-gray-600 mb-4 text-center">
                Accede a la base de datos más completa de obras y proyectos en todo México, actualizada diariamente.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Datos verificados y actualizados</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Cobertura nacional</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Información detallada de cada proyecto</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaProjectDiagram className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Soluciones para cada necesidad</h3>
              <p className="text-gray-600 mb-4 text-center">
                Diferentes planes adaptados a las necesidades específicas de tu empresa.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>INFORTE Estándar</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>INFORTE PYME</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>INFORTE Enterprise</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-[#0066CC] text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Oportunidades de negocio</h3>
              <p className="text-gray-600 mb-4 text-center">
                Encuentra nuevas oportunidades de negocio y haz crecer tu empresa.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Contactos directos con tomadores de decisiones</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Licitaciones y proyectos privados</span>
                </li>
                <li className="flex items-start">
                  <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                  <span>Alertas personalizadas</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section id="services" className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Nuestros Servicios</h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-20 h-20 bg-[#0066CC]/10 flex items-center justify-center rounded-full mx-auto mb-6">
                <FaBuilding className="text-[#0066CC] text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-4">OBRAS</h3>
              <p className="text-gray-600 mb-6">
                Información detallada sobre obras en construcción, licitaciones y proyectos en todo México.
              </p>
              <a href="#" className="inline-block text-[#0066CC] font-semibold hover:underline">
                Conocer más <FaArrowRight className="inline ml-1" />
              </a>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-20 h-20 bg-[#0066CC]/10 flex items-center justify-center rounded-full mx-auto mb-6">
                <FaProjectDiagram className="text-[#0066CC] text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-4">PROYECTOS</h3>
              <p className="text-gray-600 mb-6">
                Base de datos de proyectos en fase de planeación, con contactos clave y detalles técnicos.
              </p>
              <a href="#" className="inline-block text-[#0066CC] font-semibold hover:underline">
                Conocer más <FaArrowRight className="inline ml-1" />
              </a>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-20 h-20 bg-[#0066CC]/10 flex items-center justify-center rounded-full mx-auto mb-6">
                <FaUsers className="text-[#0066CC] text-3xl" />
              </div>
              <h3 className="text-2xl font-bold mb-4">PUBLICIDAD DIRIGIDA</h3>
              <p className="text-gray-600 mb-6">
                Alcanza a tu público objetivo con campañas de marketing especializadas para el sector construcción.
              </p>
              <a href="#" className="inline-block text-[#0066CC] font-semibold hover:underline">
                Conocer más <FaArrowRight className="inline ml-1" />
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section id="benefits" className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Beneficios para tu empresa</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-[#0066CC]/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-4">
                <FaFileContract className="text-[#0066CC] text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Nuevas oportunidades de negocio</h3>
                <p className="text-gray-600">
                  Descubre proyectos potenciales y licitaciones antes que tu competencia, expandiendo tu cartera de clientes y aumentando tus ventas.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-12 h-12 bg-[#0066CC]/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-4">
                <FaUsers className="text-[#0066CC] text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Contactos directos</h3>
                <p className="text-gray-600">
                  Accede a información de contacto verificada de tomadores de decisiones en empresas constructoras, desarrolladoras y entidades gubernamentales.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-12 h-12 bg-[#0066CC]/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-4">
                <FaChartLine className="text-[#0066CC] text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Inteligencia de mercado</h3>
                <p className="text-gray-600">
                  Analiza tendencias del sector, monitorea a tu competencia y planifica estratégicamente tus esfuerzos comerciales con datos actualizados.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-12 h-12 bg-[#0066CC]/10 rounded-lg flex-shrink-0 flex items-center justify-center mr-4">
                <FaHeadset className="text-[#0066CC] text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Asesoría especializada</h3>
                <p className="text-gray-600">
                  Recibe soporte de nuestro equipo de expertos que te ayudarán a aprovechar al máximo la información disponible para generar oportunidades de negocio.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" className="py-14 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Tienes dudas o deseas contratar una suscripción?</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            Solicita una demostración personalizada y descubre cómo INFORTE puede ayudarte a encontrar nuevas oportunidades de negocio.
          </p>
          
          <div className="max-w-lg mx-auto bg-white/10 backdrop-blur-sm p-8 rounded-lg">
            <form className="space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">Nombre completo</label>
                <input type="text" className="w-full px-4 py-2 rounded-md bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" placeholder="Tu nombre" />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Correo electrónico</label>
                <input type="email" className="w-full px-4 py-2 rounded-md bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" placeholder="correo@ejemplo.com" />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Teléfono</label>
                <input type="tel" className="w-full px-4 py-2 rounded-md bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" placeholder="(55) XXXX-XXXX" />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Empresa</label>
                <input type="text" className="w-full px-4 py-2 rounded-md bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" placeholder="Nombre de tu empresa" />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Mensaje</label>
                <textarea rows="4" className="w-full px-4 py-2 rounded-md bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#0066CC]" placeholder="¿En qué podemos ayudarte?"></textarea>
              </div>
              
              <button type="submit" className="w-full bg-[#0066CC] hover:bg-[#0077DD] text-white font-bold py-3 px-4 rounded-md transition-colors">
                Solicitar Demostración
              </button>
            </form>
          </div>
          
          <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-6 text-gray-300 text-sm">
            <div className="flex items-center">
              <FaHeadset className="mr-2 text-xl" />
              <span>Llamanos al +52.55.7965.8530</span>
            </div>
            
            <div className="flex items-center">
              <FaEnvelope className="mr-2 text-xl" />
              <a href="mailto:fhernandez@ecosoft.com.mx" className="hover:text-white transition-colors">fhernandez@ecosoft.com.mx</a>
            </div>
            
            <div className="flex items-center">
              <FaMapMarkerAlt className="mr-2 text-xl" />
              <span>Watteau 54, Nonoalco Mixcoac, Del. Benito Juárez, C.P. 03700</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Inforte; 